"use strict";
// The following code is adpated from https://github.com/transloadit/uppy/blob/master/packages/%40uppy/aws-s3-multipart/src/MultipartUploader.js
// which is released under a MIT License (https://github.com/transloadit/uppy/blob/master/LICENSE)
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var url_join_1 = __importDefault(require("url-join"));
var MB = 1024 * 1024;
var getChunkSize = function (file) { return Math.ceil(file.size / 10000); };
var createMultipartUpload = function (file, s3UploadDir, endpoint) {
    var csrftoken = (document.getElementsByName("csrfmiddlewaretoken")[0]).value;
    var headers = new Headers({
        accept: "application/json",
        "content-type": "application/json",
        "X-CSRFToken": csrftoken
    });
    return fetch(endpoint, {
        method: "post",
        headers: headers,
        body: JSON.stringify({
            filename: file.name,
            contentType: file.type,
            s3UploadDir: s3UploadDir
        })
    })
        .then(function (response) {
        return response.json();
    })
        .then(function (data) {
        return data;
    });
};
var listParts = function (_a) {
    var key = _a.key, uploadId = _a.uploadId, endpoint = _a.endpoint;
    var filename = encodeURIComponent(key);
    var uploadIdEnc = encodeURIComponent(uploadId);
    var url = (0, url_join_1.default)(endpoint, uploadIdEnc, "?key=".concat(filename));
    return fetch(url, { method: "get" })
        .then(function (response) {
        return response.json();
    })
        .then(function (data) {
        return data["parts"];
    });
};
var abortMultipartUpload = function (_a) {
    var key = _a.key, uploadId = _a.uploadId, endpoint = _a.endpoint;
    var filename = encodeURIComponent(key);
    var uploadIdEnc = encodeURIComponent(uploadId);
    var csrftoken = (document.getElementsByName("csrfmiddlewaretoken")[0]).value;
    var headers = new Headers({
        "X-CSRFToken": csrftoken
    });
    var url = (0, url_join_1.default)(endpoint, uploadIdEnc, "?key=".concat(filename));
    return fetch(url, {
        method: "delete",
        headers: headers
    }).then(function (response) {
        return response.json();
    });
};
var prepareUploadPart = function (_a) {
    var key = _a.key, uploadId = _a.uploadId, number = _a.number, endpoint = _a.endpoint;
    var filename = encodeURIComponent(key);
    var csrftoken = (document.getElementsByName("csrfmiddlewaretoken")[0]).value;
    var headers = new Headers({ "X-CSRFToken": csrftoken });
    var url = (0, url_join_1.default)(endpoint, uploadId, "".concat(number), "?key=".concat(filename));
    return fetch(url, {
        method: "get",
        headers: headers
    })
        .then(function (response) {
        return response.json();
    })
        .then(function (data) {
        return data;
    });
};
var completeMultipartUpload = function (_a) {
    var key = _a.key, uploadId = _a.uploadId, parts = _a.parts, endpoint = _a.endpoint;
    var filename = encodeURIComponent(key);
    var uploadIdEnc = encodeURIComponent(uploadId);
    var csrftoken = (document.getElementsByName("csrfmiddlewaretoken")[0]).value;
    var headers = new Headers({
        "X-CSRFToken": csrftoken
    });
    var url = (0, url_join_1.default)(endpoint, uploadIdEnc, "complete", "?key=".concat(filename));
    return fetch(url, {
        method: "post",
        headers: headers,
        body: JSON.stringify({
            parts: parts
        })
    })
        .then(function (response) {
        return response.json();
    })
        .then(function (data) {
        return data;
    });
};
function remove(arr, el) {
    var i = arr.indexOf(el);
    if (i !== -1) {
        arr.splice(i, 1);
    }
}
var S3Uploader = /** @class */ (function () {
    function S3Uploader(file, options) {
        this.options = options;
        this.file = file;
        this.key = this.options.key || null;
        this.uploadId = this.options.uploadId || null;
        this.parts = [];
        this.s3UploadDir = this.options.s3UploadDir;
        this.endpoint = this.options.endpoint;
        // Do `this.createdPromise.then(OP)` to execute an operation `OP` _only_ if the
        // upload was created already. That also ensures that the sequencing is right
        // (so the `OP` definitely happens if the upload is created).
        //
        // This mostly exists to make `_abortUpload` work well: only sending the abort request if
        // the upload was already created, and if the createMultipartUpload request is still in flight,
        // aborting it immediately after it finishes.
        this.createdPromise = Promise.reject();
        this.isPaused = false;
        this.chunks = [];
        this.chunkState = [];
        this.uploading = [];
        this._initChunks();
        this.createdPromise.catch(function () { return ({}); }); // silence uncaught rejection warning
    }
    S3Uploader.prototype._initChunks = function () {
        var chunks = [];
        var desiredChunkSize = getChunkSize(this.file);
        // at least 5MB per request, at most 10k requests
        var minChunkSize = Math.max(5 * MB, Math.ceil(this.file.size / 10000));
        var chunkSize = Math.max(desiredChunkSize, minChunkSize);
        for (var i = 0; i < this.file.size; i += chunkSize) {
            var end = Math.min(this.file.size, i + chunkSize);
            chunks.push(this.file.slice(i, end));
        }
        this.chunks = chunks;
        this.chunkState = chunks.map(function () { return ({
            uploaded: 0,
            busy: false,
            done: false
        }); });
    };
    S3Uploader.prototype._createUpload = function () {
        var _this = this;
        this.createdPromise = new Promise(function (resolve) { return resolve(); }).then(function () {
            return createMultipartUpload(_this.file, _this.s3UploadDir, _this.endpoint);
        });
        return this.createdPromise
            .then(function (result) {
            var valid = typeof result === "object" &&
                result &&
                typeof result.uploadId === "string" &&
                typeof result.key === "string";
            if (!valid) {
                throw new TypeError("AwsS3/Multipart: Got incorrect result from `createMultipartUpload()`, expected an object `{ uploadId, key }`.");
            }
            _this.key = result.key;
            _this.uploadId = result.uploadId;
            if (_this.options.onStart) {
                _this.options.onStart(result);
            }
            _this._uploadParts();
        })
            .catch(function (err) {
            _this._onError(err);
        });
    };
    S3Uploader.prototype._resumeUpload = function () {
        var _this = this;
        if (!this.key || !this.uploadId) {
            return Promise.resolve();
        }
        return listParts({
            uploadId: this.uploadId,
            key: this.key,
            endpoint: this.endpoint
        })
            .then(function (parts) {
            parts.forEach(function (part) {
                var i = part.PartNumber - 1;
                _this.chunkState[i] = {
                    uploaded: part.Size,
                    etag: part.ETag,
                    done: true,
                    busy: false
                };
                // Only add if we did not yet know about this part.
                if (!_this.parts.some(function (p) { return p.PartNumber === part.PartNumber; })) {
                    _this.parts.push({
                        ETag: part.ETag,
                        PartNumber: part.PartNumber
                    });
                }
            });
            _this._uploadParts();
        })
            .catch(function (err) {
            _this._onError(err);
        });
    };
    S3Uploader.prototype._uploadParts = function () {
        var _this = this;
        if (this.isPaused) {
            return;
        }
        var need = (this.options.limit || 1) - this.uploading.length;
        if (need === 0) {
            return;
        }
        // All parts are uploaded.
        if (this.chunkState.every(function (state) { return state.done; })) {
            void this._completeUpload();
            return;
        }
        var candidates = [];
        for (var i = 0; i < this.chunkState.length; i++) {
            var state = this.chunkState[i];
            if (state.done || state.busy)
                continue;
            candidates.push(i);
            if (candidates.length >= need) {
                break;
            }
        }
        candidates.forEach(function (index) {
            void _this._uploadPart(index);
        });
    };
    S3Uploader.prototype._uploadPart = function (index) {
        var _this = this;
        this.chunkState[index].busy = true;
        if (!this.key || !this.uploadId) {
            return Promise.resolve();
        }
        return prepareUploadPart({
            key: this.key,
            uploadId: this.uploadId,
            number: index + 1,
            endpoint: this.endpoint
        })
            .then(function (result) {
            var valid = typeof result === "object" &&
                result &&
                typeof result.url === "string";
            if (!valid) {
                throw new TypeError("AwsS3/Multipart: Got incorrect result from `prepareUploadPart()`, expected an object `{ url }`.");
            }
            return result;
        })
            .then(function (_a) {
            var url = _a.url;
            _this._uploadPartBytes(index, url);
        }, function (err) {
            _this._onError(err);
        });
    };
    S3Uploader.prototype._onPartProgress = function (index, sent) {
        this.chunkState[index].uploaded = sent;
        if (this.options.onProgress) {
            var totalUploaded = this.chunkState.reduce(function (n, c) { return n + c.uploaded; }, 0);
            this.options.onProgress(totalUploaded, this.file.size);
        }
    };
    S3Uploader.prototype._onPartComplete = function (index, etag) {
        this.chunkState[index].etag = etag;
        this.chunkState[index].done = true;
        var part = {
            PartNumber: index + 1,
            ETag: etag
        };
        this.parts.push(part);
        if (this.options.onPartComplete) {
            this.options.onPartComplete(part);
        }
        this._uploadParts();
    };
    S3Uploader.prototype._uploadPartBytes = function (index, url) {
        var _this = this;
        var body = this.chunks[index];
        var xhr = new XMLHttpRequest();
        xhr.open("PUT", url, true);
        xhr.responseType = "text";
        this.uploading.push(xhr);
        xhr.upload.addEventListener("progress", function (ev) {
            if (!ev.lengthComputable)
                return;
            _this._onPartProgress(index, ev.loaded);
        });
        xhr.addEventListener("abort", function (ev) {
            remove(_this.uploading, ev.target);
            _this.chunkState[index].busy = false;
        });
        xhr.addEventListener("load", function (ev) {
            var target = ev.target;
            remove(_this.uploading, target);
            _this.chunkState[index].busy = false;
            if (target.status < 200 || target.status >= 300) {
                _this._onError(new Error("Non 2xx"));
                return;
            }
            _this._onPartProgress(index, body.size);
            // NOTE This must be allowed by CORS.
            var etag = target.getResponseHeader("ETag");
            if (etag === null) {
                _this._onError(new Error("AwsS3/Multipart: Could not read the ETag header. This likely means CORS is not configured correctly on the S3 Bucket. See https://uppy.io/docs/aws-s3-multipart#S3-Bucket-Configuration for instructions."));
                return;
            }
            _this._onPartComplete(index, etag);
        });
        xhr.addEventListener("error", function (ev) {
            remove(_this.uploading, ev.target);
            _this.chunkState[index].busy = false;
            var error = new Error("Unknown error");
            // error.source = ev.target
            _this._onError(error);
        });
        xhr.send(body);
    };
    S3Uploader.prototype._completeUpload = function () {
        var _this = this;
        // Parts may not have completed uploading in sorted order, if limit > 1.
        this.parts.sort(function (a, b) { return a.PartNumber - b.PartNumber; });
        if (!this.uploadId || !this.key) {
            return Promise.resolve();
        }
        return completeMultipartUpload({
            key: this.key,
            uploadId: this.uploadId,
            parts: this.parts,
            endpoint: this.endpoint
        }).then(function (result) {
            if (_this.options.onSuccess) {
                _this.options.onSuccess(result);
            }
        }, function (err) {
            _this._onError(err);
        });
    };
    S3Uploader.prototype._onError = function (error) {
        if (this.options.onError) {
            this.options.onError(error);
        }
        else {
            throw error;
        }
    };
    S3Uploader.prototype.start = function () {
        this.isPaused = false;
        if (this.uploadId) {
            void this._resumeUpload();
        }
        else {
            void this._createUpload();
        }
    };
    S3Uploader.prototype.pause = function () {
        var inProgress = this.uploading.slice();
        inProgress.forEach(function (xhr) {
            xhr.abort();
        });
        this.isPaused = true;
    };
    S3Uploader.prototype.abort = function () {
        var _this = this;
        this.uploading.slice().forEach(function (xhr) {
            xhr.abort();
        });
        this.createdPromise.then(function () {
            if (_this.key && _this.uploadId) {
                void abortMultipartUpload({
                    key: _this.key,
                    uploadId: _this.uploadId,
                    endpoint: _this.endpoint
                });
            }
        }, function () {
            // if the creation failed we do not need to abort
        });
        this.uploading = [];
    };
    return S3Uploader;
}());
exports.default = S3Uploader;
